import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../services/supabase';

const ProfileSettings = ({ 
  profile, 
  sharingEnabled, 
  setSharingEnabled, 
  notificationsEnabled, 
  setNotificationsEnabled,
  onProfileUpdate 
}) => {
  const [isPrivate, setIsPrivate] = useState(profile?.is_private || false);
  const navigate = useNavigate();
  
  // Local state for editable profile fields
  const [editProfile, setEditProfile] = useState({
    name: '',
    bio: '',
    gender: '',
    homeLocation: ''
  });
  const [hasProfileChanges, setHasProfileChanges] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // When profile loads, sync editProfile state
  useEffect(() => {
    if (profile) {
      setEditProfile({
        name: profile.name || '',
        bio: profile.bio || '',
        gender: profile.gender || '',
        homeLocation: profile.homeLocation || ''
      });
      setHasProfileChanges(false);
      setSaveSuccess(false);
    }
  }, [profile]);

  const handleChangePhoto = () => {
    navigate("/profile-setup");
  };

  const handleToggleSharing = () => {
    setSharingEnabled(!sharingEnabled);
  };

  const handleToggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
  };

  const handleTogglePrivacy = async () => {
    try {
      const newPrivacySetting = !isPrivate;
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_private: newPrivacySetting })
        .eq('user_id', profile.user_id);
      
      if (error) {
        console.error('Error updating privacy setting:', error);
        alert('Error updating privacy setting. Please try again.');
      } else {
        setIsPrivate(newPrivacySetting);
        console.log('Privacy setting updated:', newPrivacySetting);
      }
    } catch (error) {
      console.error('Error updating privacy setting:', error);
      alert('Error updating privacy setting. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error.message);
      alert('Error signing out. Please try again.');
    }
  };

  // Handler for editable fields
  const handleEditProfileChange = (field, value) => {
    setEditProfile(prev => {
      const updated = { ...prev, [field]: value };
      // Check if any field is different from profile
      const changed =
        updated.name !== (profile.name || '') ||
        updated.bio !== (profile.bio || '') ||
        updated.gender !== (profile.gender || '') ||
        updated.homeLocation !== (profile.homeLocation || '');
      setHasProfileChanges(changed);
      setSaveSuccess(false);
      return updated;
    });
  };

  // Save changes handler
  const handleSaveProfileChanges = async () => {
    setSavingProfile(true);
    setSaveSuccess(false);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          name: editProfile.name,
          bio: editProfile.bio,
          gender: editProfile.gender,
          homeLocation: editProfile.homeLocation
        })
        .eq('user_id', profile.user_id);
      if (!error) {
        // Update the parent profile state
        if (onProfileUpdate) {
          onProfileUpdate(editProfile);
        }
        setHasProfileChanges(false);
        setSaveSuccess(true);
      } else {
        alert('Failed to save changes: ' + error.message);
      }
    } catch (err) {
      alert('Unexpected error saving profile changes.');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="settings-content">
      {/* Profile Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Profile</h2>
        <div className="settings-row">
          <div className="settings-picture-container">
            {profile.profilePicture ? (
              <img 
                src={profile.profilePicture} 
                alt="Profile" 
                className="profile-picture" 
                style={{ width: "60px", height: "60px" }}
                onError={(e) => {
                  console.error("Profile image failed to load:", profile.profilePicture);
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/60?text=Profile";
                }}
              />
            ) : (
              <div className="profile-picture-placeholder" style={{ width: "60px", height: "60px", fontSize: "1.5rem" }}>
                {profile.username ? profile.username.charAt(0).toUpperCase() : "?"}
              </div>
            )}
          </div>
          <button className="change-photo-btn" onClick={handleChangePhoto}>
            Change Photo
          </button>
        </div>
        <div className="settings-row">
          <div className="settings-label">Username:</div>
          <div className="settings-value">{profile.username}</div>
        </div>
        <div className="settings-row">
          <div className="settings-label">Display Name:</div>
          <div className="settings-value">
            <input
              type="text"
              value={editProfile.name}
              onChange={e => handleEditProfileChange('name', e.target.value)}
              className="settings-input"
              placeholder="Display Name"
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">Bio:</div>
          <div className="settings-value">
            <textarea
              value={editProfile.bio}
              onChange={e => handleEditProfileChange('bio', e.target.value)}
              className="settings-textarea"
              placeholder="Bio"
              rows={3}
              style={{ resize: 'vertical', minHeight: 48 }}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">Gender:</div>
          <div className="settings-value">
            <select
              value={editProfile.gender}
              onChange={e => handleEditProfileChange('gender', e.target.value)}
              className="settings-select"
            >
              <option value="">Not specified</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">Location:</div>
          <div className="settings-value">
            <input
              type="text"
              value={editProfile.homeLocation}
              onChange={e => handleEditProfileChange('homeLocation', e.target.value)}
              className="settings-input"
              placeholder="Location"
            />
          </div>
        </div>
      </div>

      {/* Privacy Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Privacy</h2>
        <div className="settings-row">
          <div className="settings-label">Profile Privacy:</div>
          <div className="settings-value">
            Private Account (only followers can see your posts)
            <label className="toggle-switch" style={{ marginLeft: "10px" }}>
              <input 
                type="checkbox" 
                checked={isPrivate}
                onChange={handleTogglePrivacy}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">Sharing:</div>
          <div className="settings-value">
            Allow others to download your maps
            <label className="toggle-switch" style={{ marginLeft: "10px" }}>
              <input 
                type="checkbox" 
                checked={profile.sharing_enabled || sharingEnabled}
                onChange={handleToggleSharing}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Notifications</h2>
        <div className="settings-row">
          <div className="settings-label">Notifications:</div>
          <div className="settings-value">
            Allow Pioneer.com to send you notifications
            <label className="toggle-switch" style={{ marginLeft: "10px" }}>
              <input 
                type="checkbox" 
                checked={profile.notifications_enabled || notificationsEnabled}
                onChange={handleToggleNotifications}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Sign Out Section */}
      <div className="settings-section" style={{ marginTop: "2rem", borderTop: "1px solid #eee", paddingTop: "2rem" }}>
        <button 
          className="sign-out-btn" 
          onClick={handleSignOut}
          style={{ 
            padding: "0.8rem 1.5rem", 
            backgroundColor: "#f44336", 
            color: "white", 
            border: "none", 
            borderRadius: "4px", 
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Save Changes Button */}
      {hasProfileChanges && (
        <div className="settings-row" style={{ justifyContent: 'flex-end', border: 'none', background: 'none' }}>
          <button
            className="save-changes-btn"
            onClick={handleSaveProfileChanges}
            disabled={savingProfile}
            style={{
              padding: '0.7rem 1.6rem',
              backgroundColor: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: savingProfile ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              marginTop: '1.2rem',
              boxShadow: '0 2px 8px rgba(66,133,244,0.08)'
            }}
          >
            {savingProfile ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
      
      {saveSuccess && (
        <div className="settings-row" style={{ color: '#388e3c', fontWeight: 'bold', background: 'none', border: 'none' }}>
          Changes saved!
        </div>
      )}
    </div>
  );
};

export default ProfileSettings; 